import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import Button from './Button/Button';
import { IconError, IconClose, IconCheckCircle, IconWarning, IconInfo } from './icons/Icons';
import { toTitleCase } from '../../helpers/stringHelper';

const activeIconMap = {
    success: IconCheckCircle,
    warning: IconWarning,
    error: IconError,
    info: IconInfo
}

export default class Notification extends React.PureComponent {
    static propTypes = {
        /** Sets the color scheme and title, if none is provided */
        type: PropTypes.oneOf(['error', 'warning', 'success', 'info']).isRequired,

        /** The notification's title */
        title: PropTypes.string,

        /** The heading element used for the title (h2, h3, etc.) */
        headingLevel: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),

        /** The notification's body */
        children: PropTypes.node,

        /** Function to fire when the user clicks the close button. The type prop
         * is sent as a function parameter. If not passed, the close button is not
         * rendered. */
        dismissNotification: PropTypes.func,

        /** If true, the notification is styled as top-of-the-page banner notification
         * that is full-width and has a solid color background */
        isBanner: PropTypes.bool,

        /** If true, the alert will gain focus when it mounts, which will
         * automatically cause the page to scroll to the notification and screen
         * readers will read the notification */
        shouldFocusOnMount: PropTypes.bool,

        /** Adds given id to outer container */
        id: PropTypes.string,

        /** Optional className added to the outer container */
        className: PropTypes.string
    };

    static defaultProps = {
        title: undefined,
        headingLevel: 3,
        dismissNotification: undefined,
        isBanner: false,
        shouldFocusOnMount: false,
        className: undefined,
        id: undefined
    };

    constructor(props) {
        super(props);

        this.ref = React.createRef();
    }

    componentDidMount() {
        const { shouldFocusOnMount } = this.props;

        if (shouldFocusOnMount && this.ref) {
            this.ref.current.focus();
        }
    }

    dismissNotification = () => {
        const { dismissNotification, type } = this.props;

        dismissNotification(type);
    }

    renderBody = () => {
        const { children } = this.props;

        if (typeof children === 'string') {
            return (
                <p>{children}</p>
            )
        }

        return (
            <div>
                {children}
            </div>
        )
    }

    render() {
        const {
            type,
            title,
            headingLevel,
            dismissNotification,
            isBanner,
            className,
            children,
            id,
            ...rest
        } = this.props;

        const HeadingElement = `h${headingLevel}`;
        const Icon = activeIconMap[type];

        if (isBanner) {
            return (
                <div
                    tabIndex="-1"
                    ref={this.ref}
                    className={cx(`rec-${type}-full-banner`, 'sarsa-no-focus-style', {
                        [className]: className
                    })}>
                    <div
                        id={id}
                        className="rec-msg"
                        aria-live="polite"
                        aria-atomic="true"
                        {...rest}>
                        <Icon className={`rec-icon-${type}`} />
                        <div className="rec-msg-text">
                            <HeadingElement className="rec-msg-text-heading">
                                {title || `${toTitleCase(type)} Notification:`}
                            </HeadingElement>

                            <div>
                                {children}
                            </div>
                        </div>
                    </div>

                    {dismissNotification && (
                        <Button
                            className="rec-notification-close-new"
                            appearance="subtle"
                            size="xs"
                            iconBeforeElement={<IconClose />}
                            onClick={this.dismissNotification}
                            screenReaderTextAfter="Dismiss notification" />
                    )}
                </div>
            );
        }

        return (
            <div
                tabIndex="-1"
                ref={this.ref}
                className={cx(`rec-notification-${type}`, 'sarsa-no-focus-style', {
                    [className]: className
                })}>
                <div
                    className='rec-notification-content'
                    aria-live="polite"
                    aria-atomic="true">
                    <span className="rec-notification-icon">
                        <Icon className={`rec-icon-${type}`} />
                    </span>

                    <div className="rec-notification-body">
                        <HeadingElement className="rec-notification-title">
                            {title || `${toTitleCase(type)} Notification:`}
                        </HeadingElement>

                        {this.renderBody()}
                    </div>
                </div>

                {dismissNotification && (
                    <Button
                        className="rec-notification-close-new"
                        appearance="subtle"
                        size="xs"
                        iconBeforeElement={<IconClose />}
                        onClick={this.dismissNotification}
                        screenReaderTextAfter="Dismiss notification" />
                )}
            </div>
        );
    }
}


